import BaseView from '../../../js/base-view';

import Field from './fields/field';
import FieldState from './fields/field-state';

const SELECTOR_COUNTRY_FIELD = '[data-field-country]';
const SELECTOR_STATE_FIELD = '[data-field-state]';
const SELECTOR_UPDATE_CHECKOUT_FIELD = '[data-update-checkout]';

const COOKIE_WOOCOMMERCE_MULTICURRENCY = 'woocommerce_multicurrency_forced_currency';
const COOKIE_CURRENCY_WORLD = 'supt-currency-world';
const DEFAULT_CURRENCY = 'CHF';

/**
 * Custom implementation of WooCommerce address-i18n & country-select scripts
 *
 * @see https://github.com/woocommerce/woocommerce/blob/4.0.1/assets/js/frontend/address-i18n.js
 * @see https://github.com/woocommerce/woocommerce/blob/4.0.1/assets/js/frontend/country-select.js
 */
export default class FormAddress extends BaseView {
	init() {
		if (typeof window.wc_country_select_params === 'undefined' ||
			typeof window.wc_address_i18n_params === 'undefined') {
			return false;
		}

		this.config = {
			states: JSON.parse(window.wc_country_select_params.countries.replace(/&quot;/g, '"')),
			locale: JSON.parse(window.wc_address_i18n_params.locale.replace(/&quot;/g, '"')),
			locale_fields: JSON.parse(window.wc_address_i18n_params.locale_fields.replace(/&quot;/g, '"')),
		};

		this.refs = {
			address_1: this.createField(this.el.querySelector(this.config.locale_fields.address_1)),
			address_2: this.createField(this.el.querySelector(this.config.locale_fields.address_2)),
			city: this.createField(this.el.querySelector(this.config.locale_fields.city)),
			postcode: this.createField(this.el.querySelector(this.config.locale_fields.postcode)),
			state: this.createField(this.el.querySelector(SELECTOR_STATE_FIELD), FieldState),
		};

		// Ne jamais rattraper « state » avec un Field générique (pas de updateOptions).
		Object.keys(this.config.locale.default).forEach((key) => {
			if (key === 'state') {
				return;
			}
			if (!this.refs[key]) {
				this.refs[key] = this.createField(this.el.querySelector(`[id*="${key}"]`));
			}
		});

		if (!this.refs.country) {
			return false;
		}

		this.bindEvents();

		const country = this.refs.country.getValue();
		this.updateStateLocale(country);
		this.reorderFields(country);

		return this;
	}

	createField(el, FieldClass = Field) {
		if (!el) {
			return null;
		}
		return new FieldClass(el);
	}

	bindEvents() {
		this.on('change', `${ SELECTOR_COUNTRY_FIELD } select`, (e) => this.onCountryChange(e.target.value));
		this.on('change', `${ SELECTOR_UPDATE_CHECKOUT_FIELD }`, this.updateCheckout.bind(this));
	}

	onCountryChange(country) {
		this.updateFieldLocale('address_1', this.config.locale[ country ]);
		this.updateFieldLocale('address_2', this.config.locale[ country ]);
		this.updateFieldLocale('city', this.config.locale[ country ]);
		this.updateFieldLocale('postcode', this.config.locale[ country ]);
		this.updateStateLocale(country);
		this.reorderFields(country);
	}

	updateFieldLocale(fieldId, locale) {
		const field = this.refs[ fieldId ];
		if (!field) {
			return;
		}

		const fieldLocale = ((locale && locale[ fieldId ]) || this.config.locale.default[ fieldId ]);

		field.setRequired(fieldLocale.required);
		field.changeLabel(fieldLocale.label);
		field[ fieldLocale.hidden ? 'hide' : 'show' ]();
	}

	updateStateLocale(country) {
		if (!(this.refs.state instanceof FieldState)) {
			return;
		}

		const locale = { ...this.config.locale.default.state, ...(this.config.locale[ country ] ? this.config.locale[ country ].state : {}) };

		this.refs.state.updateOptions(this.config.states[ country ]);
		this.refs.state.setRequired(!!locale.required);
		if (locale.label) {
			this.refs.state.changeLabel(locale.label);
		}
	}

	reorderFields(country) {
		const fields = { ...this.config.locale.default, ...(this.config.locale[ country ] ? this.config.locale[ country ] : {}) };

		for (const [key, value] of Object.entries(fields)) {
			if (this.refs[key]) {
				this.refs[key].setPriority(value.priority);
			}
		}
	}

	getCurrencyConfig() {
		return (window.supt && window.supt.currency) ? window.supt.currency : null;
	}

	currencyFromCountry(country) {
		const config = this.getCurrencyConfig();
		const euCountries = (config && config.eu_countries) ? config.eu_countries : [];
		const defaultCurrency = (config && config.default) ? config.default : DEFAULT_CURRENCY;

		let currency = defaultCurrency;
		if (country !== 'CH' && euCountries.indexOf(country) !== -1) {
			currency = 'EUR';
		}

		return {
			currency,
			isWorld: (country !== 'CH' && currency === defaultCurrency),
		};
	}

	setCurrencyCookie(name, value, expiryDays = 365) {
		const exdate = new Date();
		exdate.setHours(exdate.getHours() + ((typeof expiryDays !== 'number' ? 365 : expiryDays) * 24));

		let domain = null;
		if (window.supt && window.supt.cookie_domain) {
			domain = window.supt.cookie_domain;
		}

		document.cookie = `${ name }=${ value };expires=${ exdate.toUTCString() };path=/${ domain ? `;domain=${ domain }` : '' };sameSite=Strict`;
	}

	syncBillingCurrencyCookies(country) {
		const { currency, isWorld } = this.currencyFromCountry(country);
		const config = this.getCurrencyConfig();
		const forcedCookie = (config && config.cookies && config.cookies.forced)
			? config.cookies.forced
			: COOKIE_WOOCOMMERCE_MULTICURRENCY;
		const worldCookie = (config && config.cookies && config.cookies.world)
			? config.cookies.world
			: COOKIE_CURRENCY_WORLD;

		this.setCurrencyCookie(forcedCookie, currency);
		this.setCurrencyCookie(worldCookie, isWorld ? 'true' : 'false');

		return currency;
	}

	/**
	 * Si « adresse différente » est décochée, aligne le pays shipping dans le DOM
	 * pour que le POST update_checkout porte la bonne destination (devis Monde, etc.).
	 */
	syncShippingCountryFromBilling(country) {
		const shipDifferent = document.getElementById('ship-to-different-address-checkbox');
		if (shipDifferent && shipDifferent.checked) {
			return;
		}

		const shippingCountry = document.getElementById('shipping_country');
		if (shippingCountry && shippingCountry.value !== country) {
			shippingCountry.value = country;
		}
	}

	triggerUpdateCheckout() {
		if (typeof window.jQuery !== 'undefined') {
			window.jQuery(document.body).trigger('update_checkout');
		}
		else {
			document.body.dispatchEvent(new CustomEvent('update_checkout', { bubbles: true }));
		}
	}

	async updateCheckout(event) {
		const isBilling = event.target.dataset.updateCheckout === 'billing';
		const country = event.target.value;

		if (isBilling) {
			try {
				if (window.supt && window.supt.restAPI && window.supt.restAPI.user) {
					await window.fetch(`${window.supt.restAPI.root}/supt/v1/users/${window.supt.restAPI.user}`, {
						credentials: 'same-origin',
						method: 'POST',
						headers: {
							'X-WP-Nonce': window.supt.restAPI.nonce,
							'Accept': 'application/json',
							'Content-Type': 'application/json'
						},
						body: JSON.stringify({ billing_country: country })
					});
				}
			}
			catch (err) {
				// Ne pas bloquer le recalcul checkout si le REST échoue.
				console.warn('WCFormAddress: billing_country meta update failed', err);
			}

			this.syncBillingCurrencyCookies(country);
			this.syncShippingCountryFromBilling(country);
			document.dispatchEvent(new CustomEvent('update-currency'));
		}

		this.triggerUpdateCheckout();
	}
}
